// ═══════════════════════════════════════════════════════════════════════════
// CONGÉ PARENTAL — « concrètement, ça donne quoi ? »
// ───────────────────────────────────────────────────────────────────────────
// 📏 POURQUOI, mesuré le 02/09/2026 sur la Search Console (3 mois) :
//   congé parental belgique 2026 .... 918 aff. · 7 clics · pos  7,7
//   conge parental 2026 ............. 635 aff. · 9 clics · pos  8,0
//   congé parental 2026 belgique .... 276 aff. · 4 clics · pos  8,2
//   congé parental .................. 207 aff. · 0 clic  · pos 26,7
//   (six requêtes : 2 597 affichages, 27 clics — CTR 1,04 %)
//
// Qui sort devant nous, vérifié le 02/09 : l'ONEm (la source officielle),
// MesAllocations.be et mes-aides-financieres.be (deux agrégateurs), Shyfter et
// iambeezy (deux SaaS). ⚠️ NI Securex, NI Partena, NI UCM.
//
// 🔑 ON NE BAT PAS L'ONEm SUR SON PROPRE TEXTE, et il ne faut pas essayer : sur
// « quel est le droit », la source officielle sera toujours première, et c'est
// sain. Ce que ni elle ni les agrégateurs ne donnent, c'est le CHIFFRE POUR CE
// CAS-CI : ce que le travailleur touche, ce que l'employeur paie encore, et le
// total. Tous décrivent ; personne ne calcule.
//
// ⚠️ AUCUN MONTANT N'EST ÉCRIT ICI. Tout vient de LB.congesThematiques.parental
// (barèmes ONEm indexés au 01.03.2026, lus le 18/08/2026, bases légales citées
// dans lois-belges.js). R1 : une valeur légale n'a qu'une vie.
//
// ⚠️ ET LA FORME « 1/10 » REND `null`, EXPRÈS. lois-belges.js écrit noir sur
// blanc que son montant n'a pas été lu au tableau ONEm et qu'il ne faut PAS le
// déduire d'une règle de trois : les barèmes ONEm ne sont pas proportionnels —
// l'allocation du mi-temps n'est pas la moitié de celle du congé complet au
// centime près, il suffit de les diviser dans lois-belges pour le voir. Ce
// module propage donc cette ignorance au lieu de la combler : l'écran dira
// « montant non publié ici », jamais un chiffre plausible.
//
// ⚠️ Les montants ne sont PAS recopiés dans ce commentaire, et ce n'est pas du
// zèle : `outil-conge-parental-branche.test.js` refuse tout barème ONEm écrit
// dans ce fichier, commentaires compris. Il ne sait pas distinguer une prose
// d'un calcul — et c'est tant mieux, parce qu'un montant cité en commentaire
// se périme à la prochaine indexation et se lit ensuite comme un fait.
// ═══════════════════════════════════════════════════════════════════════════
import { LB } from '@/app/lib/lois-belges';
import { round2 } from '@/app/lib/money';

const PAR = LB.congesThematiques.parental;

/** La part du temps de travail QUI RESTE, par forme. C'est la définition même
 *  de chaque forme (un mi-temps est un mi-temps), pas une valeur légale à
 *  sourcer. Le 1/10 n'est pas dans lois-belges côté montant, mais sa fraction
 *  de temps, elle, est dans son nom. */
const PART_TRAVAILLEE = { complet: 0, miTemps: 0.5, cinquieme: 0.8, dixieme: 0.9 };

/** Les formes, telles que lois-belges les publie. */
export function formesCongeParental() {
  return PAR.formes.map((f) => ({
    id: f.id,
    label: f.label,
    dureeMoisMax: f.dureeMoisMax,
    fractionMois: f.fractionMois,
    partTravaillee: PART_TRAVAILLEE[f.id] ?? null,
    // null quand l'ONEm n'a pas été lu sur ce barème — surtout ne pas deviner.
    allocationBrute: f.brut,
    allocationNette: f.net,
    montantConnu: f.brut !== null && f.net !== null,
  }));
}

function nombre(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Ce que donne un congé parental, pour un salaire et une forme donnés.
 *
 * @returns {object|null} null si le salaire manque — jamais un zéro trompeur.
 */
export function simulerCongeParental({ brutMensuel, forme } = {}) {
  const brut = nombre(brutMensuel);
  if (brut === null) return null;

  const f = formesCongeParental().find((x) => x.id === forme);
  if (!f) return null;

  // Le salaire suit le temps de travail restant : c'est le mécanisme de la
  // forme, pas un barème. Une suspension complète laisse 0.
  const salaireMaintenu = round2(brut * f.partTravaillee);

  return {
    forme: f,
    brutInitial: brut,
    salaireMaintenu,
    // ⚠️ `null` se propage : si l'ONEm n'a pas été lu pour cette forme, on ne
    // peut PAS annoncer un total. Mieux vaut un trou qu'un chiffre inventé.
    allocationBrute: f.allocationBrute,
    allocationNette: f.allocationNette,
    totalBrut: f.allocationBrute === null ? null : round2(salaireMaintenu + f.allocationBrute),
    // Ce que l'employeur paie encore : uniquement le travail presté. L'ONEm
    // verse l'allocation, l'employeur ne la complète pas (sauf CCT sectorielle).
    coutEmployeurSalaire: salaireMaintenu,
    dureeMoisMax: f.dureeMoisMax,
    fractionMois: f.fractionMois,
    // La perte de revenu brut, qui est LA question que se pose le travailleur.
    perteBrute: f.allocationBrute === null ? null : round2(brut - (salaireMaintenu + f.allocationBrute)),
  };
}

/** Les conditions d'accès, telles que lois-belges les porte. */
export function conditionsCongeParental() {
  return {
    ageEnfantMax: PAR.ageEnfantMax,
    ancienneteMois: PAR.ancienneteMois,
    ancienneteFenetreMois: PAR.ancienneteFenetreMois,
    silenceEmployeurVautAccord: PAR.silenceEmployeurVautAccord,
    source: LB.congesThematiques.source,
    indexation: LB.congesThematiques.indexation,
  };
}
